import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Globe, ArrowRight, ArrowLeft, Eye, EyeOff, Sparkles, GraduationCap, Gift, CheckCircle, MapPin, Clock, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InternationalStudentAuthPage = () => {
  const navigate = useNavigate();
  const { login: authLogin, user } = useAuth();
  
  const [step, setStep] = useState(1); // 1: email, 2: details
  const [mode, setMode] = useState('check'); // check, signin, signup
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    country: '',
    date_of_birth: '',
    birth_time: '',
    birth_place: '',
  });

  useEffect(() => {
    if (user) {
      // International students go directly to instructions (free access)
      navigate('/instructions');
    }
  }, [user, navigate]);

  // Check if email exists
  const handleEmailCheck = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/check-email`, {
        email: formData.email
      });
      
      if (response.data.exists) {
        setMode('signin');
        toast.success('Welcome back! Please enter your password.');
      } else {
        setMode('signup');
        setStep(2);
        toast.success('Let\'s create your free account!');
      }
    } catch (error) {
      // If endpoint doesn't exist, default to signup mode
      setMode('signup');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign in
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password,
      });
      
      authLogin(response.data.token, response.data.user);
      toast.success('Welcome back! Taking you to your assessment...');
      navigate('/instructions');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid credentials';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign up - free for international students
  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register-international`, {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        country: formData.country,
        date_of_birth: formData.date_of_birth,
        birth_time: formData.birth_time,
        birth_place: formData.birth_place,
      });
      
      authLogin(response.data.token, response.data.user);
      toast.success('Account created! Your free assessment is ready.');
      navigate('/instructions');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0D0D0D] to-[#1a0a28] flex items-center justify-center px-4" data-testid="intl-student-auth-page">
      {/* Animated background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full filter blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full filter blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C5A065]/5 rounded-full filter blur-[150px]" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => step === 2 && mode === 'signup' ? setStep(1) : navigate('/')}
          className="mb-6 text-gray-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {step === 2 ? 'Back' : 'Home'}
        </Button>

        {/* Hero Card */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 mb-6">
            <Globe className="h-10 w-10 text-blue-400" />
          </div>
          
          <h1 className="text-4xl font-heading font-bold text-white mb-3">
            International <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Students</span>
          </h1>
          
          <p className="text-lg text-gray-400 mb-4">
            Career Discovery Assessment
          </p>

          {/* Free Badge */}
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
            <Gift className="h-5 w-5 text-green-400" />
            <span className="text-green-400 font-semibold text-lg">100% FREE</span>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { icon: GraduationCap, label: '85 Scenarios' },
            { icon: Sparkles, label: 'AI Insights' },
            { icon: CheckCircle, label: 'Career Report' },
          ].map((feature, i) => (
            <div key={i} className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
              <feature.icon className="h-5 w-5 text-blue-400 mx-auto mb-2" />
              <span className="text-xs text-gray-400">{feature.label}</span>
            </div>
          ))}
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          
          {/* Step 1: Email */}
          {step === 1 && mode === 'check' && (
            <form onSubmit={handleEmailCheck} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">Get Started</h2>
                <p className="text-sm text-gray-500">Enter your email to begin</p>
              </div>
              
              <div>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  autoFocus
                  className="h-14 text-lg bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                  data-testid="email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl"
                data-testid="continue-btn"
              >
                {loading ? 'Checking...' : 'Continue'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          )}

          {/* Sign In Mode */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">Welcome Back!</h2>
                <p className="text-sm text-gray-500">{formData.email}</p>
              </div>
              
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  autoFocus
                  className="h-14 text-lg bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl"
                data-testid="signin-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <button
                type="button"
                onClick={() => { setMode('signup'); setStep(2); }}
                className="w-full text-sm text-gray-500 hover:text-white transition-colors"
              >
                Don't have an account? <span className="text-blue-400">Create one</span>
              </button>
            </form>
          )}

          {/* Step 2: Sign Up Details */}
          {step === 2 && mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-white mb-2">Create Free Account</h2>
                <p className="text-sm text-gray-500">{formData.email}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-400 text-sm mb-1 block">Full Name</Label>
                  <Input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Your full name"
                    required
                    className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                    data-testid="fullname-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-400 text-sm mb-1 block flex items-center gap-2">
                    <Globe className="h-3 w-3" /> Country
                  </Label>
                  <Input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., USA, UK, Canada"
                    required
                    className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                    data-testid="country-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-400 text-sm mb-1 block flex items-center gap-2">
                    <Calendar className="h-3 w-3" /> Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                    data-testid="dob-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-400 text-sm mb-1 block flex items-center gap-2">
                    <Clock className="h-3 w-3" /> Birth Time <span className="text-gray-600 text-xs">(Optional)</span>
                  </Label>
                  <Input
                    type="time"
                    value={formData.birth_time}
                    onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                    className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                    data-testid="birthtime-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-400 text-sm mb-1 block flex items-center gap-2">
                    <MapPin className="h-3 w-3" /> Birth Place (City, Country)
                  </Label>
                  <Input
                    type="text"
                    value={formData.birth_place}
                    onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                    placeholder="e.g., New York, USA"
                    required
                    className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl"
                    data-testid="birthplace-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-400 text-sm mb-1 block">Create Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 6 characters"
                      required
                      minLength={6}
                      className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 rounded-xl pr-12"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl mt-2"
                data-testid="signup-btn"
              >
                {loading ? 'Creating Account...' : 'Start Free Assessment'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default InternationalStudentAuthPage;
